import React, {useState, memo, useEffect} from "react";
import FolderIcon from "@mui/icons-material/Folder";
import ListItemText from "@mui/material/ListItemText";
import {Checkbox, Grid, ListItemButton} from "@mui/material";
import {
  Asset,
  deleteAsset,
  toggleFolderVisibility,
  updateAsset
} from "../redux/assets-slice";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import List from "@mui/material/List";
import {AddAssetList, AssetItem} from ".";
import {useDispatch} from 'react-redux';
import {
  CheckboxWithLabel,
  DenseIcon,
  DenseMenu,
  InputWithLabel,
  MenuDivider,
  NestedMenu,
  PromptDialog,
  SimpleDialog
} from '../components';
import {MenuItem, Typography} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PublicOffIcon from '@mui/icons-material/PublicOff';
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from '@mui/icons-material/Add';
import {createFolder, deleteFolder, Folder, updateFolder} from '../redux/folders-slice';
import { isElectronApp, isStaticSite } from "../electron-modules";
import { useTranslation } from "react-i18next";
import { getLocalizedFolderName, isAssetFile, useAssetTools } from "../utilities";
import slash from "slash";
import { useContextMenu, useDialog } from "../hooks";

export const assetFolderClass = "asset-folder";

interface NewFolderDialogProps {
  open: boolean;
  onClose: () => void;
  folder?: Folder;
}

/** Dialog for creating and editing folder properties */
export const FolderDialog = (props: NewFolderDialogProps) => {
  const {open, onClose, folder} = props;

  const dispatch = useDispatch();
  const {t} = useTranslation();

  const [name, setName] = useState("");
  const [allowAnonViewers, setAllowAnonViewers] = useState(true);

  const handleSubmit = () => {
    if (folder) {
      // Update existing folder
      dispatch(updateFolder({
        folderID: folder.id,
        name,
        allowAnonViewers
      }));
    } else {
      // Create a new folder
      dispatch(createFolder(name, allowAnonViewers));
    }
    onClose();
  };

  useEffect(() => {
    if (!open) return;

    if (folder) {
      const localizedFolderName = getLocalizedFolderName(folder.name);
      setName(localizedFolderName);
      setAllowAnonViewers(folder.allowAnonViewers);
    } else {
      setName("");
      setAllowAnonViewers(true);
    }
  }, [open]);

  const canSubmit = !!name;

  const title = folder
    ? t('folder.edit-title')
    : t("folder.create-title");

  return (
    <SimpleDialog
      open={open}
      onClose={onClose}
      onSubmit={handleSubmit}
      canSubmit={canSubmit}
      maxWidth={"xs"}
      title={title}
      button={t('buttons.submit')}
    >
      <Grid container spacing={2}>

        {/** Title */}
        <Grid item xs={12}>
          <InputWithLabel
            title={t("folder-list.folder-prompt-label")}
            value={name}
            onChange={setName}
          />
        </Grid>

        {/** Anonymous access */}
        <Grid item xs={12}>
          <CheckboxWithLabel
            title={t('folder.allow_anonymous_access')}
            label={t('folder.anonymous_access_description')}
            value={allowAnonViewers}
            onChange={setAllowAnonViewers}
          />
        </Grid>

      </Grid>
    </SimpleDialog>
  );
};


interface AssetFolderProps {
  folder: Folder;
  assets: Asset[];
  draggingAsset: boolean;
  setDraggingAsset: React.Dispatch<string>
}

export const AssetFolder = memo((props: AssetFolderProps) => {
  const {folder, assets, draggingAsset, setDraggingAsset} = props;

  const dispatch = useDispatch();
  const {t} = useTranslation();
  const {addAssetPaths} = useAssetTools();
  const contextMenu = useContextMenu();
  const deleteDialog = useDialog();
  const editDialog = useDialog();

  const [expanded, setExpanded] = useState(true);
  const [dragDropHover, setDragDropHover] = useState(false);

  const updateAssetFolder = (assetID) => {
    dispatch(updateAsset({
      assetID,
      folderID: folder.id
    }));
  };

  const addAssetsToFolder = (event) => {
    const assetPaths = [...event.dataTransfer.files]
      .map(file => slash(file.path))
      .filter(isAssetFile);

    addAssetPaths(assetPaths, folder.id);
  };

  const handleClick = (event) => {
    const isCheckBox = (event.target.type === "checkbox");
    if (isCheckBox) return;

    setExpanded(!expanded);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragDropHover(true);
  };

  const handleDragLeave = (event) => {
    setDragDropHover(false);
  };

  const handleDrop = (event) => {
    if (isStaticSite) return;

    setDragDropHover(false);

    if (draggingAsset) {
      updateAssetFolder(draggingAsset);
    } else {
      addAssetsToFolder(event);
    }
  };

  const handleDeleteSubmit = () => {
    deleteDialog.handleClose();

    for (let asset of assets) {
      dispatch(deleteAsset(asset.id));
    }

    dispatch(deleteFolder(folder.id));
  };

  const handleDeleteClick = () => {
    contextMenu.handleClose();
    deleteDialog.handleOpen();
  };

  const handleEditClick = () => {
    contextMenu.handleClose();
    editDialog.handleOpen();
  };

  const toggleCheckboxes = () => {
    let newVisibility;
    if (numVisible > 0 && numHidden > 0) {
      newVisibility = true;
    } else if (numVisible > 0) {
      newVisibility = false;
    } else {
      newVisibility = true;
    }

    dispatch(toggleFolderVisibility({
      folderID: folder.id,
      visible: newVisibility
    }));
  };

  const numVisible = assets.filter(x => x.visible).length;
  const numHidden = assets.filter(x => !x.visible).length;

  // Folder is visible if it is user made or if it contains assets
  const visible = folder.default ? assets.length > 0 : true;

  const localizedFolderName = getLocalizedFolderName(folder.name);

  return (
    <React.Fragment>
      {visible && (<React.Fragment>
        <ListItemButton
          dense
          className={assetFolderClass}
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onContextMenu={contextMenu.handleOpen}
          selected={dragDropHover}
        >
          <DenseIcon>
            <FolderIcon fontSize="small"/>
          </DenseIcon>

          {!folder.allowAnonViewers && <DenseIcon>
            <PublicOffIcon fontSize="small"/>
          </DenseIcon>}

          <ListItemText primary={localizedFolderName}/>

          {(assets.length > 0) && (<React.Fragment>
            {/* Show / hide contents */}
            {expanded ? <ExpandLess/> : <ExpandMore/>}

            {/* Toggle all folder assets */}
            <Checkbox
              edge="end"
              checked={numVisible > 0}
              onChange={toggleCheckboxes}
              indeterminate={numVisible > 0 && numHidden > 0}
            />
          </React.Fragment>)}

        </ListItemButton>
      </React.Fragment>)}

      {visible && expanded &&  (<React.Fragment>
        <List component="div" disablePadding dense>
          {assets.map(asset => <AssetItem
            key={asset.id}
            asset={asset}
            setDraggingAsset={setDraggingAsset}
          />)}
        </List>
      </React.Fragment>)}

      <DenseMenu
        open={contextMenu.open && isElectronApp}
        onClose={contextMenu.handleClose}
        anchorPosition={contextMenu.anchorPosition}
      >
        {/** New asset submenu */}
        <NestedMenu
          text={t('folder.add_assets')}
          icon={<AddIcon fontSize="small"/>}
          parentMenuOpen={contextMenu.open}
        >
          <AddAssetList
            folderID={folder.id}
            closeContextMenu={contextMenu.handleClose}
          />
        </NestedMenu>

        {/** Functions submenu */}
        {/*
        {<FunctionsMenu
          assets={assets}
          contextOpen={contextMenu.open}
          closeContextMenu={contextMenu.handleClose}
        />}
        */}

        {isElectronApp && <MenuDivider/>}

        {/** Edit folder */}
        <MenuItem onClick={handleEditClick}>
          <DenseIcon>
            <EditIcon fontSize="small"/>
          </DenseIcon>
          <Typography variant="inherit">
            {t("folder.edit")}
          </Typography>
        </MenuItem>

        {/** Delete folder */}
        <MenuItem onClick={handleDeleteClick}>
          <DenseIcon>
            <DeleteIcon fontSize="small"/>
          </DenseIcon>
          <Typography variant="inherit">
            {t("folder.delete")}
          </Typography>
        </MenuItem>

      </DenseMenu>

      <FolderDialog
        open={editDialog.open}
        onClose={editDialog.handleClose}
        folder={folder}
      />

      <PromptDialog
        open={deleteDialog.open}
        onCancel={deleteDialog.handleClose}
        onSubmit={handleDeleteSubmit}
        title={t("folder.remove-title")}
        prompt={t("folder.remove-prompt")}
        button={t("buttons.delete")}
      />


    </React.Fragment>
  );
});
